import stakingABI from "./staking_abi.json";
import nctTokenABI from "./NCTToken.json";
import purchaseManagerABI from "./PurchaseManager copy.json";

// 环境变量检查
const ENV = process.env.REACT_APP_ENV || "dev"; // 默认为开发环境
const isDev = ENV === "dev";
const isRelease = ENV === "release";

// 合约地址配置 - 根据环境选择
export const CONTRACTS = isDev
  ? {
      // 测试网合约地址
      STAKING: "0xa2c2Cebbe516dBF9e80835238Ed3aDe860b22F6a" as const, // ⚠️ 请更新为测试网质押合约地址
      NCT_TOKEN: "0x73b8C8c5c81F257832e86A7329123035477C12fA" as const, // ⚠️ 请更新为测试网NCT代币合约地址
      SkinNFT: "0xD0482AEb564fD12690c7E39C1daC4268640F3d36" as const,
      BlindBoxNFT: "0x9B2Df93Bf6B11Dd4b352c0bb4bA81D5B7a5bD6Ae" as const,
      MatchResultRegistry:
        "0xA355c6DBC6f7D29ed62a92DEd2637416d59cED89" as const,
      PurchaseManager: "0x16fFe5c941A2A34541d39D5fE5BA19a6a9869509" as const,
      TreasuryAddress: "0x0547cc921af57684113c03dE5a4ef57f4dF4dB5c" as const,
    }
  : ({
      // 主网合约地址
      STAKING: "0x0fa24ac9b668b210366b2E700a1641a2e6Db1174" as const, // 主网质押合约地址
      NCT_TOKEN: "0x4343939a19a1d8d32b17a0e8958b2f4ea1604444" as const, // 主网NCT代币合约地址
      SkinNFT: "0x3700Fe697DEae7C791d3F974fa4db5aC6eEe0cA8" as const,
      BlindBoxNFT: "0x2536970d096eEEd89C9139D4991cFd3c7b0b5cfA" as const,
      MatchResultRegistry:
        "0x0AaA1978bbbFC8f136A963558269E569d6a30945" as const,
      PurchaseManager: "0x657A80C703baF756a76cc18Ea5052F1f5215de0b" as const,
      TreasuryAddress: "0x0547cc921af57684113c03dE5a4ef57f4dF4dB5c" as const,
    } as const);

// ABI导入
export const ABIS = {
  STAKING: stakingABI,
  NCT_TOKEN: nctTokenABI,
  PURCHASE_MANAGER: purchaseManagerABI,
} as const;

// BSC主网RPC池配置
export const BSC_MAINNET_RPC_POOL = [
  "https://rpc.ankr.com/bsc/861a4f3afee73437812056e2efd748f76db6220c614f0e34a9c7b2f66c9e97d5",
  "https://rpc.ankr.com/bsc/45608a7cdae0c7873dcbd9196953b7015f8d7127736df17480472224b128e67e",
  "https://rpc.ankr.com/bsc/6912fa941610bbd399c033a47c0103ac0611db7e30b95c19b8087d5363670537",
  "https://rpc.ankr.com/bsc/c5c5e3d8c1b1b285ba4778fb16c35fc211b3eb691cf9bd2a9b9a155eca0f4bc2",
] as const;

// BSC测试网RPC池配置 - 移除有问题的Ankr端点，重新排序
export const BSC_TESTNET_RPC_POOL = [
  // BNB Chain 官方节点 - 主要节点（最稳定）
  "https://bsc-testnet-dataseed.bnbchain.org",
  "https://bsc-testnet.bnbchain.org",

  // 第三方公共RPC - 经过测试的稳定节点
  "https://bsc-testnet-rpc.publicnode.com",
  "https://endpoints.omniatech.io/v1/bsc/testnet/public",

  // BNB Chain 官方节点 - 备用节点
  "https://data-seed-prebsc-1-s1.bnbchain.org:8545",
  "https://data-seed-prebsc-2-s1.bnbchain.org:8545",
  "https://data-seed-prebsc-1-s2.bnbchain.org:8545",
  "https://data-seed-prebsc-2-s2.bnbchain.org:8545",
  "https://data-seed-prebsc-1-s3.bnbchain.org:8545",
  "https://data-seed-prebsc-2-s3.bnbchain.org:8545",
] as const;

// 根据环境选择RPC池
export const CURRENT_RPC_POOL = isDev
  ? BSC_TESTNET_RPC_POOL
  : BSC_MAINNET_RPC_POOL;

// 网络配置 - 根据环境选择
export const NETWORK_CONFIG = isDev
  ? {
      // 测试网配置
      chainId: 97, // BSC测试网链ID
      name: "BSC Testnet",
      rpcUrls: BSC_TESTNET_RPC_POOL,
      primaryRpcUrl: BSC_TESTNET_RPC_POOL[0], // 主要RPC
      explorerUrl: "https://testnet.bscscan.com/", // 测试网浏览器
      nativeCurrency: {
        name: "tBNB",
        symbol: "tBNB", // 测试网BNB
        decimals: 18,
      },
    }
  : ({
      // 主网配置
      chainId: 56, // BSC主网链ID
      name: "BSC Mainnet",
      rpcUrls: BSC_MAINNET_RPC_POOL,
      primaryRpcUrl: BSC_MAINNET_RPC_POOL[0], // 主要RPC
      explorerUrl: "https://bscscan.com/", // 主网浏览器
      nativeCurrency: {
        name: "BNB",
        symbol: "BNB", // 主网BNB
        decimals: 18,
      },
    } as const);

// 导出环境信息
export const ENVIRONMENT = {
  ENV,
  isDev,
  isRelease,
  chainId: NETWORK_CONFIG.chainId,
  networkName: NETWORK_CONFIG.name,
  explorerUrl: NETWORK_CONFIG.explorerUrl,
} as const;

// RPC健康检查和故障转移工具
export class RPCManager {
  private static instance: RPCManager;
  private currentRpcIndex = 0;
  private failedRpcs = new Set<number>();
  private lastHealthCheck = 0;
  private readonly healthCheckInterval = 5 * 60 * 1000; // 5分钟

  static getInstance(): RPCManager {
    if (!RPCManager.instance) {
      RPCManager.instance = new RPCManager();
    }
    return RPCManager.instance;
  }

  // 获取当前可用的RPC URL
  getCurrentRPC(): string {
    return CURRENT_RPC_POOL[this.currentRpcIndex];
  }

  // 获取所有可用的RPC URLs
  getAvailableRPCs(): string[] {
    return CURRENT_RPC_POOL.filter((_, index) => !this.failedRpcs.has(index));
  }

  // 标记当前RPC为失败并切换到下一个
  markCurrentRPCFailed(): string {
    console.warn(`RPC ${this.getCurrentRPC()} 标记为失败，切换到下一个节点`);

    this.failedRpcs.add(this.currentRpcIndex);
    this.switchToNextRPC();

    return this.getCurrentRPC();
  }

  // 切换到下一个可用的RPC
  private switchToNextRPC(): void {
    const availableIndices = CURRENT_RPC_POOL.map((_, index) => index).filter(
      (index) => !this.failedRpcs.has(index)
    );

    if (availableIndices.length === 0) {
      console.warn("所有RPC节点都失败，重置失败列表");
      this.failedRpcs.clear();
      this.currentRpcIndex = 0;
      return;
    }

    const currentAvailableIndex = availableIndices.indexOf(
      this.currentRpcIndex
    );
    const nextIndex = (currentAvailableIndex + 1) % availableIndices.length;
    this.currentRpcIndex = availableIndices[nextIndex];
  }

  // 健康检查
  async healthCheck(): Promise<void> {
    const now = Date.now();
    if (now - this.lastHealthCheck < this.healthCheckInterval) {
      return;
    }

    this.lastHealthCheck = now;

    const healthPromises = CURRENT_RPC_POOL.map(async (rpc, index) => {
      try {
        const response = await fetch(rpc, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            jsonrpc: "2.0",
            method: "eth_blockNumber",
            params: [],
            id: 1,
          }),
          signal: AbortSignal.timeout(5000), // 5秒超时
        });

        if (response.ok) {
          const data = await response.json();
          if (data.result) {
            // RPC恢复正常，从失败列表中移除
            if (this.failedRpcs.has(index)) {
              this.failedRpcs.delete(index);
            }
            return { index, status: "healthy", rpc };
          }
        }
        throw new Error("Invalid response");
      } catch (error) {
        console.warn(`RPC ${rpc} 健康检查失败:`, error);
        this.failedRpcs.add(index);
        return { index, status: "failed", rpc };
      }
    });

    const results = await Promise.allSettled(healthPromises);
    const healthyCount = results.filter(
      (r) => r.status === "fulfilled" && r.value.status === "healthy"
    ).length;

    console.log(
      `RPC健康检查完成: ${healthyCount}/${CURRENT_RPC_POOL.length} 节点正常`
    );
  }

  // 获取RPC状态统计
  getStats() {
    return {
      total: CURRENT_RPC_POOL.length,
      available: CURRENT_RPC_POOL.length - this.failedRpcs.size,
      failed: this.failedRpcs.size,
      current: this.getCurrentRPC(),
      failedRpcs: Array.from(this.failedRpcs).map((i) => CURRENT_RPC_POOL[i]),
      environment: ENVIRONMENT.networkName,
    };
  }
}
